export const POISONED_DATA_TARGETS = [
  { value: "property_snapshots", label: "Property snapshots" },
  { value: "properties", label: "Properties" },
  { value: "scrape_queue", label: "Scrape queue" },
  { value: "page_snapshots", label: "Page snapshots" },
  { value: "suburb_profile_snapshots", label: "Suburb profile snapshots" },
] as const;

export type PoisonedDataTargetTable = (typeof POISONED_DATA_TARGETS)[number]["value"];

export interface PoisonedDataParameters {
  targetTable?: PoisonedDataTargetTable;
  adapterSlug?: string;
  postcode?: string;
  suburb?: string;
  minPrice?: number | null;
  maxPrice?: number | null;
  textContains?: string;
  /**
   * Raw PostgREST OR-syntax fragment appended to the structured filters,
   * e.g. `raw_data->>address.eq.null,sale_price.eq.0`. Server-side
   * `safeWhereClause` enforces an allowlist + length cap + keyword
   * blocklist. See docs/poisoned-data-raw-filter-and-saved-searches.md.
   */
  whereClause?: string;
  operatorGoal?: string;
}

export const DEFAULT_METHOD_PARAMETERS: Record<string, PoisonedDataParameters> = {
  domain_title_pollution_snapshots: {
    targetTable: "property_snapshots",
    adapterSlug: "domain-com-au",
  },
  price_outlier_snapshots: {
    targetTable: "property_snapshots",
    minPrice: 50_000,
    maxPrice: 50_000_000,
  },
  implausible_sale_price: {
    targetTable: "property_snapshots",
    minPrice: 50_000,
  },
  impossible_sale_date: {
    targetTable: "property_snapshots",
  },
  negative_or_absurd_counts: {
    targetTable: "property_snapshots",
  },
  address_trailer_pollution: {
    targetTable: "property_snapshots",
  },
  literal_null_strings: {
    targetTable: "property_snapshots",
  },
  sale_date_equals_page_date: {
    targetTable: "property_snapshots",
  },
  fake_fixture_locations: {
    targetTable: "properties",
  },
  completed_queue_without_snapshot: {
    targetTable: "scrape_queue",
  },
  missing_core_sale_fields: {
    targetTable: "property_snapshots",
  },
  custom_filter_builder: {
    targetTable: "property_snapshots",
  },
  ai_filter_suggestion: {
    targetTable: "property_snapshots",
  },
};

export function normalizePoisonedDataParameters(
  methodSlug: string,
  parameters: PoisonedDataParameters = {},
): PoisonedDataParameters {
  const defaults = DEFAULT_METHOD_PARAMETERS[methodSlug] ?? DEFAULT_METHOD_PARAMETERS.custom_filter_builder;
  const merged = { ...defaults, ...parameters };
  return {
    ...merged,
    adapterSlug: merged.adapterSlug?.trim() || undefined,
    postcode: merged.postcode?.replace(/\D/g, "").slice(0, 4) || undefined,
    suburb: merged.suburb?.trim() || undefined,
    textContains: merged.textContains?.trim() || undefined,
    whereClause: merged.whereClause?.trim() || undefined,
    minPrice: toOptionalNumber(merged.minPrice),
    maxPrice: toOptionalNumber(merged.maxPrice),
  };
}

function toOptionalNumber(value: unknown): number | null | undefined {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

export function getTargetLabel(target: string | null | undefined): string {
  return POISONED_DATA_TARGETS.find((item) => item.value === target)?.label ?? target ?? "Unknown";
}
